/**
 * The reading settings, as a control the reader can reach *while reading*.
 *
 * Two entry points, one set of controls, and the split between them is the point: `/settings`
 * is where a player goes to change their language, and it is the wrong place to adjust a line
 * length, because judging a line length means looking at the story. So `ReadingControls` is
 * the fieldsets, `ReadingSettings` is the same fieldsets in a drawer over the feed, and the
 * settings page mounts the first one under its own heading.
 *
 * The sample line is not decoration. Every control here changes the story column, which is
 * behind the drawer's backdrop while the drawer is open — a preview that is covered by the
 * thing previewing it. One paragraph in the narration face, inside the panel, is what makes
 * the choice visible at the moment it is made.
 */
import { useState } from 'react';
import {
  MEASURE_STEPS,
  SIZE_STEPS,
  useReadingStore,
  type NarrationFace,
  type NarrationSize,
  type ReadingMeasure,
} from '@/features/settings/reading';
import { useT } from '@/i18n/context';
import { Dialog } from '@/ui/Dialog';

const SIZES = Object.keys(SIZE_STEPS) as NarrationSize[];
const MEASURES = Object.keys(MEASURE_STEPS) as ReadingMeasure[];
const FACES: NarrationFace[] = ['serif', 'sans'];

/** The toggle plus the drawer — what the story screen mounts. */
export function ReadingSettings() {
  const t = useT();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
        }}
        className="border-border bg-bg/90 text-fg-muted hover:border-accent hover:text-accent z-20 border px-3 py-1.5 font-mono backdrop-blur transition-colors"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {t('ui.nav.reading')}
      </button>

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        title={t('ui.reading.title')}
        variant="drawer"
        side="right"
      >
        <ReadingControls />
      </Dialog>
    </>
  );
}

/** The controls themselves, so the settings page can hold the same ones. */
export function ReadingControls() {
  const t = useT();
  const size = useReadingStore((state) => state.size);
  const measure = useReadingStore((state) => state.measure);
  const face = useReadingStore((state) => state.face);
  const set = useReadingStore((state) => state.set);
  const reset = useReadingStore((state) => state.reset);

  return (
    <div className="flex flex-col gap-6">
      <Picker
        name="reading_size"
        legend={t('ui.reading.size')}
        options={SIZES}
        value={size}
        label={(option) => t(`ui.reading.size.${option}`)}
        onPick={(next) => {
          set('size', next);
        }}
      />

      <Picker
        name="reading_measure"
        legend={t('ui.reading.measure')}
        options={MEASURES}
        value={measure}
        label={(option) => t(`ui.reading.measure.${option}`)}
        onPick={(next) => {
          set('measure', next);
        }}
      />

      <Picker
        name="reading_face"
        legend={t('ui.reading.face')}
        options={FACES}
        value={face}
        label={(option) => t(`ui.reading.face.${option}`)}
        onPick={(next) => {
          set('face', next);
        }}
      />

      {/*
        The preview. It reads the same three custom properties the feed does, so it is not an
        approximation of the setting — it is the setting, at the width the panel allows.
      */}
      <p
        className="border-rule text-fg border-t pt-5"
        style={{
          fontFamily: 'var(--font-narration)',
          fontSize: 'var(--text-narration)',
          lineHeight: 'var(--leading-narration)',
        }}
      >
        {t('ui.reading.sample')}
      </p>

      <div className="flex items-center justify-between gap-4">
        <p className="text-fg-faint" style={{ fontSize: 'var(--text-meta)' }}>
          {t('ui.reading.stored_here')}
        </p>
        <button
          type="button"
          onClick={reset}
          className="text-fg-faint hover:text-fg shrink-0 font-mono transition-colors"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          {t('ui.reading.reset')}
        </button>
      </div>
    </div>
  );
}

/**
 * A segmented radio group — real `<input type="radio">`s inside styled labels, which is what
 * buys arrow-key navigation, the group's accessible name and the "3 of 4" a screen reader
 * announces. The same shape `CharacterCreation` uses; the focus ring that a `sr-only` input
 * would otherwise hide is put back by one rule in `index.css`.
 */
function Picker<T extends string>({
  name,
  legend,
  options,
  value,
  label,
  onPick,
}: {
  name: string;
  legend: string;
  options: readonly T[];
  value: T;
  label: (option: T) => string;
  onPick: (option: T) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-fg-muted font-mono" style={{ fontSize: 'var(--text-meta)' }}>
        {legend}
      </legend>
      <div className="mt-1 flex flex-wrap gap-2">
        {options.map((option) => (
          <label
            key={option}
            className={`cursor-pointer border px-3 py-1.5 font-mono transition-colors ${
              value === option
                ? 'border-accent text-accent'
                : 'border-border text-fg-muted hover:border-fg-faint'
            }`}
            style={{ fontSize: 'var(--text-meta)' }}
          >
            <input
              type="radio"
              name={name}
              value={option}
              checked={value === option}
              onChange={() => {
                onPick(option);
              }}
              className="sr-only"
            />
            {label(option)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
